import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import { visuallyHidden } from '@mui/utils';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Notification from '../Notification';
import { setLoaderMessage } from '../../utils/loaderManager';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: 'tds',
    numeric: false,
    disablePadding: false,
    label: 'TDS?',
    align: 'center',
  },
  {
    id: 'payroll',
    numeric: false,
    disablePadding: false,
    label: 'Payroll',
    align: 'center',
  },
  {
    id: 'empName',
    numeric: false,
    disablePadding: false,
    label: 'Name',
    align: 'left',
  },
  {
    id: 'employeeCode',
    numeric: true,
    disablePadding: false,
    label: 'Employee Code',
    align: 'center',
  },
];

function EnhancedTableHead(props) {
  const { 
    order, 
    orderBy, 
    onRequestSort, 
    onSelectAllPayroll, 
    allPayrollSelected, 
    somePayrollSelected,
    onSelectAllTds,
    allTdsSelected,
    someTdsSelected,
    tdsEnabled
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            padding={headCell.id === 'payroll' || headCell.id === 'tds' ? 'none' : (headCell.disablePadding ? 'none' : 'normal')}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              background: '#f8fafc',
              color: '#1565c0',
              fontWeight: 600,
              fontSize: '0.8rem',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              borderBottom: '2px solid #e3f2fd',
              py: 0.75,
              ...(headCell.id === 'payroll' || headCell.id === 'tds' ? { width: 100, minWidth: 100, px: 0 } : {})
            }}
          >
            {headCell.id === 'payroll' ? (
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', pl: 1 }}>
                <Checkbox
                  size="small"
                  indeterminate={somePayrollSelected && !allPayrollSelected}
                  checked={allPayrollSelected}
                  onChange={onSelectAllPayroll}
                  inputProps={{ 'aria-label': 'select all employees for payroll' }}
                  sx={{ 
                    p: 0.25,
                    color: '#0b418e',
                    '&.Mui-checked': { color: '#0b418e' },
                    '&.MuiCheckbox-indeterminate': { color: '#0b418e' }
                  }}
                />
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#1565c0', fontSize: '0.75rem' }}>{headCell.label}</Typography>
              </Box>
            ) : headCell.id === 'tds' ? (
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', pl: 1 }}>
                <Checkbox
                  size="small"
                  indeterminate={someTdsSelected && !allTdsSelected}
                  checked={allTdsSelected}
                  onChange={onSelectAllTds}
                  disabled={!tdsEnabled}
                  inputProps={{ 'aria-label': 'select all employees for TDS' }}
                  sx={{ 
                    p: 0.25,
                    color: '#0b418e',
                    '&.Mui-checked': { color: '#0b418e' },
                    '&.MuiCheckbox-indeterminate': { color: '#0b418e' },
                    '&.Mui-disabled': { color: '#e0e0e0' }
                  }}
                />
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#1565c0', fontSize: '0.75rem' }}>{headCell.label}</Typography>
              </Box>
            ) : (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
                sx={{
                  color: '#1565c0 !important',
                  '&:hover': { color: '#0b418e' },
                  '& .MuiTableSortLabel-icon': { color: '#0b418e !important' },
                  '&.Mui-active': { color: '#1565c0' },
                  '&.Mui-active .MuiTableSortLabel-icon': { color: '#0b418e !important' }
                }}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  onSelectAllPayroll: PropTypes.func.isRequired,
  allPayrollSelected: PropTypes.bool.isRequired,
  somePayrollSelected: PropTypes.bool.isRequired,
  onSelectAllTds: PropTypes.func.isRequired,
  allTdsSelected: PropTypes.bool.isRequired,
  someTdsSelected: PropTypes.bool.isRequired,
  tdsEnabled: PropTypes.bool.isRequired,
};

function EnhancedTableToolbar(props) {
  const {
    payrollCount,
    tdsCount,
    totalCount,
    rowsPerPage,
    rowsPerPageOptions,
    page,
    count,
    onPageChange,
    onRowsPerPageChange,
  } = props;

  return (
    <Toolbar
      sx={{
        pl: 1.5,
        pr: 1.5,
        py: 0.5,
        backgroundColor: '#fafafa',
        borderBottom: '1px solid #e8e8e8',
        minHeight: '40px !important',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: '#424242',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        Select Employees
        <Typography
          component="span"
          sx={{
            fontSize: '0.8rem',
            color: '#757575',
            fontWeight: 400,
            ml: 0.75,
          }}
        >
          ({totalCount})
        </Typography>
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            backgroundColor: payrollCount > 0 ? '#e8f5e9' : 'transparent',
            px: 1.25,
            py: 0.5,
            borderRadius: '12px',
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: payrollCount > 0 ? '#4caf50' : '#bdbdbd',
            }}
          />
          <Typography variant="caption" sx={{ fontWeight: 500, color: payrollCount > 0 ? '#2e7d32' : '#9e9e9e', fontSize: '0.75rem' }}>
            Payroll: {payrollCount}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            backgroundColor: tdsCount > 0 ? '#fff3e0' : 'transparent',
            px: 1.25,
            py: 0.5,
            borderRadius: '12px',
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: tdsCount > 0 ? '#ff9800' : '#bdbdbd',
            }}
          />
          <Typography variant="caption" sx={{ fontWeight: 500, color: tdsCount > 0 ? '#e65100' : '#9e9e9e', fontSize: '0.75rem' }}>
            TDS: {tdsCount}
          </Typography>
        </Box>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          sx={{
            border: 'none',
            backgroundColor: 'transparent',
            margin: 0,
            padding: 0,
            minHeight: '32px',
            '& .MuiTablePagination-toolbar': {
              minHeight: '32px',
              paddingLeft: 0,
              paddingRight: 0,
              justifyContent: 'flex-end',
              paddingTop: 0,
              paddingBottom: 0,
            },
            '& .MuiTablePagination-spacer': {
              display: 'none',
            },
            '& .MuiTablePagination-selectLabel': {
              fontSize: '0.75rem',
              color: '#616161',
              margin: 0,
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: '0.75rem',
              color: '#616161',
              margin: 0,
              marginLeft: 1,
            },
            '& .MuiTablePagination-select': {
              fontSize: '0.75rem',
              paddingTop: '2px',
              paddingBottom: '2px',
            },
            '& .MuiTablePagination-actions': {
              marginLeft: 1,
              '& .MuiIconButton-root': {
                padding: '4px',
              },
            },
          }}
        />
      </Box>
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  payrollCount: PropTypes.number.isRequired,
  tdsCount: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  page: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
};

export default function EnhancedTable(props) {
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('empName');
  const [employees, setEmployees] = React.useState([]);
  const [rows, setRows] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(true); // Default to true for dense padding
  const [rowsPerPage, setRowsPerPage] = React.useState(50); // Default to 50 rows per page
  const [payrollChecked, setPayrollChecked] = React.useState({}); // Track Payroll checkbox per employee
  const [tdsChecked, setTdsChecked] = React.useState({}); // Track TDS checkbox per employee

  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const navigate = useNavigate();

  function createData(empID, empName, employeeCode) {
    return {
      empID,
      empName,
      employeeCode,
    };
  }

  useEffect(() => {
    async function myFunc() {
      const URL = `${process.env.REACT_APP_API_BASE_URL}/create-payroll/get-active-employees/${props.selectedMonth}/${props.selectedYear}`;
      try {
        setLoaderMessage('Loading employees for payroll...');
        const response = await axios.get(URL);
        setEmployees(response.data);
        setRows(
          response.data.map((emp) =>
            createData(emp.employee_id, emp.first_name + ' ' + emp.last_name, emp.employee_code)
          )
        );
      } catch (e) {
        if (e.response.status === 401) {
          setNotify({
            isOpen: true,
            message: 'Session Expired!',
            type: 'error',
          });
          setTimeout(() => {
            navigate('/');
          }, 1000);
        }
      }
    }
    if (props.selectedMonth !== '' && props.selectedYear !== '') {
      myFunc();
    }
  }, [props.selectedMonth, props.selectedYear, navigate]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;
  
  // Count employees selected for payroll
  const payrollSelectedCount = Object.values(payrollChecked).filter(Boolean).length;
  const allPayrollSelected = rows.length > 0 && payrollSelectedCount === rows.length;
  const somePayrollSelected = payrollSelectedCount > 0 && payrollSelectedCount < rows.length;

  // Count employees selected for TDS (only among those with payroll selected)
  const payrollSelectedIds = Object.keys(payrollChecked).filter(id => payrollChecked[id]);
  const tdsSelectedCount = payrollSelectedIds.filter(id => tdsChecked[id]).length;
  const allTdsSelected = payrollSelectedCount > 0 && tdsSelectedCount === payrollSelectedCount;
  const someTdsSelected = tdsSelectedCount > 0 && tdsSelectedCount < payrollSelectedCount;
  const tdsEnabled = payrollSelectedCount > 0; // TDS select all is only enabled if at least one payroll is selected

  // Build payload in format [{ employee_id: 301, isTds: true }, ...]
  const buildEmployeePayload = (payrollState, tdsState) => {
    return Object.keys(payrollState)
      .filter(id => payrollState[id])
      .map(id => ({
        employee_id: parseInt(id, 10),
        isTds: tdsState[id] || false
      }));
  };

  // Handle Select All Payroll checkbox change
  const handleSelectAllPayroll = (event) => {
    const isChecked = event.target.checked;
    const newPayrollChecked = {};
    const newTdsChecked = { ...tdsChecked };
    
    rows.forEach(row => {
      newPayrollChecked[row.empID] = isChecked;
      // If unchecking all, also uncheck TDS
      if (!isChecked) {
        newTdsChecked[row.empID] = false;
      }
    });
    
    setPayrollChecked(newPayrollChecked);
    
    if (!isChecked) {
      setTdsChecked(newTdsChecked);
    }
    
    // Notify parent with the new payload format
    const payload = buildEmployeePayload(newPayrollChecked, newTdsChecked);
    props.onSelectEmployees(payload);
  };

  // Handle Select All TDS checkbox change (only affects employees with payroll selected)
  const handleSelectAllTds = (event) => {
    const isChecked = event.target.checked;
    const newTdsChecked = { ...tdsChecked };
    
    // Only update TDS for employees who have payroll selected
    rows.forEach(row => {
      if (payrollChecked[row.empID]) {
        newTdsChecked[row.empID] = isChecked;
      }
    });
    
    setTdsChecked(newTdsChecked);
    
    // Notify parent with the new payload format
    const payload = buildEmployeePayload(payrollChecked, newTdsChecked);
    props.onSelectEmployees(payload);
  };

  // Handle Payroll checkbox change
  const handlePayrollChange = (event, empID) => {
    const isChecked = event.target.checked;
    const newPayrollChecked = {
      ...payrollChecked,
      [empID]: isChecked,
    };
    setPayrollChecked(newPayrollChecked);
    
    let newTdsChecked = tdsChecked;
    // If payroll is unchecked, also uncheck TDS
    if (!isChecked) {
      newTdsChecked = {
        ...tdsChecked,
        [empID]: false,
      };
      setTdsChecked(newTdsChecked);
    }
    
    // Notify parent with the new payload format
    const payload = buildEmployeePayload(newPayrollChecked, newTdsChecked);
    props.onSelectEmployees(payload);
  };

  // Handle TDS checkbox change
  const handleTdsChange = (event, empID) => {
    const newTdsChecked = {
      ...tdsChecked,
      [empID]: event.target.checked,
    };
    setTdsChecked(newTdsChecked);
    
    // Notify parent with the new payload format
    const payload = buildEmployeePayload(payrollChecked, newTdsChecked);
    props.onSelectEmployees(payload);
  };

  return (
    <Box sx={{ width: '100%', mb: 0 }}>
      <Paper 
        elevation={0}
        sx={{ 
          width: '100%', 
          mt: '4px',
          mb: 0,
          borderRadius: '8px',
          border: '1px solid #e8e8e8',
          overflow: 'hidden'
        }}
      >
        <EnhancedTableToolbar
          payrollCount={payrollSelectedCount}
          tdsCount={tdsSelectedCount}
          totalCount={rows.length}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[50, 100]}
          page={page}
          count={rows.length}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        <TableContainer sx={{ maxHeight: 460 }}>
          <Table stickyHeader sx={{ minWidth: 600 }} aria-labelledby="tableTitle" size="small">
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              onSelectAllPayroll={handleSelectAllPayroll}
              allPayrollSelected={allPayrollSelected}
              somePayrollSelected={somePayrollSelected}
              onSelectAllTds={handleSelectAllTds}
              allTdsSelected={allTdsSelected}
              someTdsSelected={someTdsSelected}
              tdsEnabled={tdsEnabled}
            />
            <TableBody>
              {stableSort(rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isSelected = payrollChecked[row.empID] || false;
                  return (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={row.empID}
                      sx={{
                        backgroundColor: isSelected 
                          ? '#f3f8ff' 
                          : index % 2 === 0 ? '#fafafa' : '#fff',
                        '&:hover': {
                          backgroundColor: isSelected ? '#e8f1fc' : '#f5f5f5',
                        },
                        '& td': {
                          borderBottom: '1px solid #e8e8e8',
                          py: 0.75,
                        },
                        '&:last-child td': {
                          borderBottom: 'none'
                        }
                      }}
                    >
                      <TableCell align="left" padding="none" sx={{ width: 100, pl: 1 }}>
                        <Checkbox
                          size="small"
                          checked={tdsChecked[row.empID] || false}
                          onChange={(event) => handleTdsChange(event, row.empID)}
                          disabled={!payrollChecked[row.empID]}
                          sx={{ 
                            p: 0.25,
                            color: '#bdbdbd',
                            '&.Mui-checked': { color: '#ff9800' },
                            '&.Mui-disabled': { color: '#e0e0e0' }
                          }}
                        />
                      </TableCell>
                      <TableCell align="left" padding="none" sx={{ width: 100, pl: 1 }}>
                        <Checkbox
                          size="small"
                          checked={payrollChecked[row.empID] || false}
                          onChange={(event) => handlePayrollChange(event, row.empID)}
                          sx={{ 
                            p: 0.25,
                            color: '#bdbdbd',
                            '&.Mui-checked': { color: '#4caf50' }
                          }}
                        />
                      </TableCell>
                      <TableCell 
                        align="left"
                        sx={{ 
                          fontWeight: isSelected ? 500 : 400,
                          color: isSelected ? '#0b418e' : '#424242',
                          fontSize: '0.8rem'
                        }}
                      >
                        {row.empName}
                      </TableCell>
                      <TableCell 
                        align="center"
                        sx={{ 
                          color: '#757575',
                          fontSize: '0.8rem'
                        }}
                      >
                        {row.employeeCode}
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Notification notify={notify} setNotify={setNotify} />
    </Box>
  );
}